// import crypto from "crypto-js"
import { generate } from "random-words"
import forge from "node-forge"
const { pki, md, util } = forge

class EncryptionHelper {
  constructor() {}

  /**
   * Function for creating a PBKDF2 key with specified bits
   * @param bits 128 | 256 | 512
   * @returns string
   */
  public createRandomKey = (bits: 128 | 256 | 512 = 256) => {
    const byteLength = bits / 8
    const randomBytes = new Uint8Array(byteLength)
    crypto.getRandomValues(randomBytes)
    return Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
  }

  // /**
  //  * Function for one way encryption
  //  * @param key string
  //  * @param salt string
  //  * @returns string
  //  */
  // public hashKey = (key: string, salt: string) => {
  //   return crypto.HmacSHA256(key, salt).toString()
  // }

  /**
   * Function for creating a simple mnemonic key for backup and recovery
   * @returns string
   */
  public createBackupKey = () => {
    return (generate(12) as string[]).join(" ")
  }

  public toUint8Array = (data: any) => {
    const dataArray = new Uint8Array(data.sigBytes)
    for (let i = 0x0; i < data.sigBytes; i++) {
      dataArray[i] = (data.words[i >>> 0x2] >>> (0x18 - (i % 0x4) * 0x8)) & 0xff
    }
    return new Uint8Array(dataArray)
  }

  public readFile(file) {
    return new Promise((res, rej) => {
      // create file reader
      let reader = new FileReader()

      // register event listeners
      reader.addEventListener("loadend", (e) => res(e?.target?.result))
      reader.addEventListener("error", rej)

      // read file
      reader.readAsArrayBuffer(file)
    })
  }

  public getPBKDFHash = async (
    payload: string,
    salt: string,
    iterations = 1,
    keySize = 8
  ) => {
    const encoder = new TextEncoder()

    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      encoder.encode(payload),
      { name: "PBKDF2" },
      false,
      ["deriveBits"]
    )

    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: "PBKDF2",
        salt: encoder.encode(salt),
        iterations,
        hash: "SHA-256",
      },
      keyMaterial,
      keySize * 32 // Vì 1 word = 4 bytes = 32 bits, nên keySize=8 → 256 bits
    )

    return Array.from(new Uint8Array(derivedBits))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
  }

  public toWordArray = (input: any) => {
    if (typeof input === "string") {
      return new TextEncoder().encode(input)
    }

    if (input instanceof ArrayBuffer) {
      return new Uint8Array(input)
    }

    if (input instanceof Uint8Array) {
      return input
    }

    throw new TypeError(
      "Unsupported input type. Must be string, ArrayBuffer, or Uint8Array."
    )
    // return crypto.lib.WordArray.create(input)
  }

  public generateKeyPair = () => {
    const { pki } = forge
    return new Promise<{ publicKey: string; privateKey: string }>((resolve) => {
      try {
        const keypair = pki.rsa.generateKeyPair(2048)
        const publicKeyPem = pki.publicKeyToPem(keypair.publicKey)
        const privateKeyPem = pki.privateKeyToPem(keypair.privateKey)
        resolve({
          publicKey: publicKeyPem,
          privateKey: privateKeyPem,
        })
      } catch (error) {
        resolve({
          publicKey: "",
          privateKey: "",
        })
      }
    })
  }

  public createSignatureAndHashFile = (
    input: any,
    isSignature = true,
    privateKey?: string
  ) => {
    try {
      const messageDigest = md.sha256.create()
      messageDigest.update(input, "utf8")
      if (isSignature && privateKey) {
        const privateKeyFromPem = pki.privateKeyFromPem(privateKey)
        const signature = privateKeyFromPem.sign(messageDigest)
        const signatureHex = util.bytesToHex(signature)
        return {
          signature: signatureHex,
          file_hash: messageDigest.digest().toHex(),
        }
      }
      return {
        signature: "",
        file_hash: messageDigest.digest().toHex(),
      }
    } catch (error) {
      console.log(error)
      return {
        signature: "",
        file_hash: "",
      }
    }
  }

  public checkVerifiedSignature = (
    publicKey: string,
    signature: string,
    input: any
  ) => {
    try {
      const signatureBytes = util.hexToBytes(signature)
      const publicKeyFromPem = forge.pki.publicKeyFromPem(publicKey)
      const digestBytes = md.sha256.create()
      digestBytes.update(input, "utf8")
      const isVerified = publicKeyFromPem.verify(
        digestBytes.digest().bytes(),
        signatureBytes
      )
      if (isVerified) {
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }
}

export const encryptionHelper = new EncryptionHelper()
